import { useState, useEffect } from 'react';
import { winchPricingAPI } from '@/api/winchPricing';
import type { WinchPricingTier, WinchPriceCalculation } from '@/types';

/**
 * Winch Pricing Calculator Component
 * Allows users to calculate winch service prices based on distance
 */
export function WinchPricingCalculator() {
  // Service IDs for winch services (verified from database)
  const WINCH_SERVICES = {
    REGULAR: 13, // Regular Winch service ID
    FULL_DOWN: 14, // Full Down Winch service ID
  };

  const [selectedService, setSelectedService] = useState<number>(WINCH_SERVICES.REGULAR);
  const [distance, setDistance] = useState<string>('');
  const [pricingTiers, setPricingTiers] = useState<WinchPricingTier[]>([]);
  const [priceCalculation, setPriceCalculation] = useState<WinchPriceCalculation | null>(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceName, setServiceName] = useState<string>('');

  // Load pricing tiers when service changes
  useEffect(() => {
    loadPricingTiers();
  }, [selectedService]);

  // Calculate price when distance changes
  useEffect(() => {
    if (distance && parseFloat(distance) > 0) {
      const timer = setTimeout(() => {
        calculatePrice();
      }, 500); // Debounce
      return () => clearTimeout(timer);
    } else {
      setPriceCalculation(null);
    }
  }, [distance, selectedService]);

  const loadPricingTiers = async () => {
    setLoading(true);
    setError(null);
    setPricingTiers([]);

    try {
      const response = await winchPricingAPI.getPricingTiers(selectedService);
      setPricingTiers(response.data.data.tiers);
      setServiceName(response.data.data.service.nameEn);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load pricing tiers');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    if (!distance || parseFloat(distance) <= 0) {
      setPriceCalculation(null);
      return;
    }

    setCalculating(true);
    setError(null);

    try {
      const response = await winchPricingAPI.calculatePrice(selectedService, parseFloat(distance));
      setPriceCalculation(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to calculate price');
      setPriceCalculation(null);
    } finally {
      setCalculating(false);
    }
  };

  const handleClear = () => {
    setDistance('');
    setPriceCalculation(null);
    setError(null);
  };

  return (
    <div className="max-w-6xl mx-auto p-6" dir="rtl">
      <div className="card">
        <h2 className="text-2xl font-bold mb-6 text-white">حاسبة أسعار خدمة الونش</h2>

        {/* Service Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            اختر خدمة الونش
          </label>
          <div className="flex gap-4">
            <button
              onClick={() => setSelectedService(WINCH_SERVICES.REGULAR)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                selectedService === WINCH_SERVICES.REGULAR
                  ? 'bg-primary text-dark shadow-glow'
                  : 'bg-dark-lighter text-gray-300 border border-gray-700 hover:border-primary'
              }`}
            >
              🚗 ونش عادي
            </button>
            <button
              onClick={() => setSelectedService(WINCH_SERVICES.FULL_DOWN)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                selectedService === WINCH_SERVICES.FULL_DOWN
                  ? 'bg-primary text-dark shadow-glow'
                  : 'bg-dark-lighter text-gray-300 border border-gray-700 hover:border-primary'
              }`}
            >
              🚙 ونش كامل
            </button>
          </div>
        </div>

        {/* Distance Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            المسافة (كيلومتر)
          </label>
          <div className="flex gap-4">
            <input
              type="number"
              step="0.1"
              min="0"
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              placeholder="أدخل المسافة بالكيلومتر"
              className="flex-1 px-4 py-3 bg-dark-lighter border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-primary transition-all text-lg"
              dir="ltr"
            />
            <button
              onClick={handleClear}
              className="px-6 py-3 bg-dark-lighter text-gray-300 rounded-lg font-semibold border border-gray-700 hover:border-primary transition-all"
            >
              مسح
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-danger/20 border border-danger rounded-lg">
            <p className="text-danger font-medium">⚠️ {error}</p>
          </div>
        )}

        {/* Price Calculation Result */}
        {priceCalculation && (
          <div className="mb-6 border border-success/30 rounded-lg overflow-hidden bg-success/10">
            <div className="bg-gradient-to-r from-success/30 to-success/20 px-6 py-4 border-b border-success/30">
              <h3 className="text-xl font-bold text-success">💰 حساب السعر</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-400 font-medium mb-1">المسافة</p>
                  <p className="text-2xl font-bold text-white" dir="ltr">{priceCalculation.distance} km</p>
                </div>
                <div>
                  <div>
                    <p className="text-sm text-gray-400 font-medium mb-1">فئة السعر</p>
                    <p className="text-lg font-semibold text-gray-300" dir="ltr">
                      {priceCalculation.tier.minDistanceKm} - {priceCalculation.tier.maxDistanceKm || '∞'} km
                    </p>
                  </div>
                  <p className="text-sm text-gray-400 font-medium mb-1">السعر الإجمالي</p>
                  <p className="text-3xl font-bold text-primary" dir="ltr">
                    {priceCalculation.price} {priceCalculation.currency}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {calculating && (
          <div className="mb-6 p-4 bg-info/20 border border-info/30 rounded-lg">
            <p className="text-info font-medium animate-pulse">⏳ جاري حساب السعر...</p>
          </div>
        )}

        {/* Pricing Tiers Table */}
        <div className="mt-8">
          <h3 className="text-xl font-bold mb-4 text-white">
            📊 فئات أسعار {serviceName || 'الخدمة'}
          </h3>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-400">جاري تحميل فئات الأسعار...</p>
            </div>
          ) : pricingTiers.length > 0 ? (
            <div className="overflow-x-auto border border-gray-700 rounded-lg">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-dark-lighter">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase tracking-wider">
                      نطاق المسافة (كم)
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase tracking-wider">
                      السعر
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase tracking-wider">
                      نوع الونش
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase tracking-wider">
                      الحالة
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-dark divide-y divide-gray-700">
                  {pricingTiers.map((tier) => (
                    <tr
                      key={tier.id}
                      className={`hover:bg-dark-lighter transition-colors ${
                        priceCalculation?.tier.id === tier.id ? 'bg-success/10 border-r-4 border-r-success' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white" dir="ltr">
                          {tier.minDistanceKm} - {tier.maxDistanceKm || '∞'} km
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-primary" dir="ltr">
                          {tier.price} KWD
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info/20 text-info border border-info/30">
                          {selectedService === WINCH_SERVICES.REGULAR ? '🚗 عادي' : '🚙 كامل'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            tier.isActive
                              ? 'bg-success/20 text-success border border-success/30'
                              : 'bg-gray-700 text-gray-400 border border-gray-600'
                          }`}
                        >
                          {tier.isActive ? '✓ نشط' : '✗ غير نشط'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-dark-lighter rounded-lg border border-gray-700">
              <p className="text-gray-400">لا توجد فئات أسعار متاحة لهذه الخدمة.</p>
            </div>
          )}
        </div>
      </div>

      {/* Usage Guide */}
      <div className="mt-8 bg-dark-lighter rounded-lg border border-gray-700 p-6">
        <h3 className="text-lg font-bold mb-4 text-white">💡 كيفية الاستخدام</h3>
        <div className="space-y-2 text-sm text-gray-300">
          <p><strong className="text-primary">1. اختر الخدمة:</strong> اختر بين الونش العادي أو الونش الكامل</p>
          <p><strong className="text-primary">2. أدخل المسافة:</strong> أدخل المسافة بالكيلومتر</p>
          <p><strong className="text-primary">3. عرض السعر:</strong> سيتم حساب السعر تلقائياً بناءً على فئات الأسعار</p>
          <p><strong className="text-primary">4. راجع الفئات:</strong> راجع جدول فئات الأسعار لرؤية جميع نطاقات المسافات المتاحة</p>
        </div>
      </div>
    </div>
  );
}
